import React from 'react';
import { Table, TableProps, Tooltip, Button, Space } from 'antd';
import { EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';

interface StandardTableProps extends TableProps<any> {
  enableFixedHeader?: boolean;
  enableSort?: boolean;
  enableRowNumber?: boolean; // New prop to enable/disable row number column
  actions?: {
    onView?: (record: any) => void;
    onEdit?: (record: any) => void;
    onDelete?: (record: any) => void;
    customActions?: Array<{
      icon: React.ReactNode;
      tooltip: string;
      onClick: (record: any) => void;
      color?: string;
      danger?: boolean;
    }>;
  };
}

const StandardTable: React.FC<StandardTableProps> = ({
  enableFixedHeader = true,
  enableSort = true,
  enableRowNumber = true, // Default to true
  actions,
  columns = [],
  pagination,
  ...tableProps
}) => {
  // Add row number column if enabled
  const rowNumberColumn = enableRowNumber ? [{
    title: '#',
    key: 'rowNumber',
    width: 60,
    fixed: 'left' as const,
    render: (_: any, __: any, index: number) => {
      const currentPage = (pagination as any)?.current || 1;
      const pageSize = (pagination as any)?.pageSize || 10;
      return (currentPage - 1) * pageSize + index + 1;
    },
  }] : [];

  // Enhanced columns with sorting if enabled
  const enhancedColumns = columns.map((col: any) => {
    if (enableSort && col.dataIndex && col.key !== 'actions') {
      return {
        ...col,
        sorter: col.sorter || ((a: any, b: any) => {
          const aVal = a[col.dataIndex];
          const bVal = b[col.dataIndex];
          
          if (typeof aVal === 'string' && typeof bVal === 'string') {
            return aVal.localeCompare(bVal);
          }
          if (typeof aVal === 'number' && typeof bVal === 'number') {
            return aVal - bVal;
          }
          if (aVal instanceof Date && bVal instanceof Date) {
            return aVal.getTime() - bVal.getTime();
          }
          return String(aVal).localeCompare(String(bVal));
        }),
        showSorterTooltip: false,
      };
    }
    return col;
  });

  // Add actions column if actions are provided
  if (actions && (actions.onView || actions.onEdit || actions.onDelete || actions.customActions)) {
    const actionsColumn = {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right' as const,
      render: (_: any, record: any) => (
        <Space size="small">
          {actions.onView && (
            <Tooltip title="View Details">
              <Button 
                type="text"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => actions.onView!(record)}
                className="standard-action-btn"
              />
            </Tooltip>
          )}
          {actions.onEdit && (
            <Tooltip title="Edit">
              <Button 
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => actions.onEdit!(record)}
                className="standard-action-btn"
              />
            </Tooltip>
          )}
          {actions.onDelete && (
            <Tooltip title="Delete">
              <Button 
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => actions.onDelete!(record)}
                className="standard-action-btn"
                danger
              />
            </Tooltip>
          )}
          {actions.customActions?.map((action, index) => (
            <Tooltip key={index} title={action.tooltip}>
              <Button 
                type="text"
                size="small"
                icon={action.icon}
                onClick={() => action.onClick(record)}
                className="standard-action-btn"
                danger={action.danger}
              />
            </Tooltip>
          ))}
        </Space>
      ),
    };
    
    enhancedColumns.push(actionsColumn);
  }

  // Combine all columns: row number + enhanced columns + actions
  const finalColumns = [...rowNumberColumn, ...enhancedColumns];

  return (
    <Table
      {...tableProps}
      columns={finalColumns}
      pagination={{
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} of ${total} items`,
        ...pagination
      }}
      scroll={{
        x: 1000,
        y: enableFixedHeader ? 600 : undefined,
        ...tableProps.scroll
      }}
      size="middle"
      bordered
    />
  );
};

export default StandardTable;
